import React, { useEffect, useRef, useState } from "react";
import { FaHandshake } from "react-icons/fa";
import MyPage from "../components/MyPage";

function Cultural() {
  const sectionRef = useRef(null);
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(null);

  useEffect(() => {
    // ✅ JSON → UI
    fetch("/api/cultural.json")
      .then(res => res.json())
      .then(json => setData(json));

    // ✅ XML → proof only
    fetch("/api/cultural.xml")
      .then(res => res.text())
      .then(() => console.log("Cultural XML loaded"));
  }, []);

  useEffect(() => {
    const banner = document.querySelector(".inner-banner");
    banner?.classList.add("animate");

    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          sectionRef.current.classList.add("in-view");
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [data]);

  if (!data) return null;

  return (
    <>
      {/* ✅ SEO – unchanged */}
      <MyPage
        title="Cultural | Thrissur Jilla Pravasi Kootayma"
        description="Cultural activities and festivals celebrating unity, tradition, and community among Thrissur Pravasi families."
        keywords="Thrissur cultural fest, Pravasi cultural activities"
      />

      {/* ===== INNER BANNER ===== */}
      <section className="inner-banner">
        <div className="inner-overlay"></div>
        <div className="inner-content">
          <h1>{data.banner.title}</h1>
        </div>
        <div className="breadcrumb">{data.banner.breadcrumb}</div>
      </section>

      {/* ===== CULTURAL SECTION ===== */}
      <section className="cultural-section" ref={sectionRef}>
        <div className="cultural-header reveal-top">
          {data.content.intro.map((text, i) => (
            <p key={i}>{text}</p>
          ))}

          <h2>
            <FaHandshake /> Celebrating Culture & Community <span></span>
          </h2>
        </div>

        <div className="cultural-grid">
          {data.gallery.map((img, index) => (
            <img
              key={index}
              src={img}
              alt="Cultural Fest"
              loading="lazy"
              className="reveal-img"
              style={{ transitionDelay: `${index * 0.08}s` }}
              onClick={() => setActiveImg(img)}
            />
          ))}
        </div>

        {activeImg && (
          <div className="lightbox" onClick={() => setActiveImg(null)}>
            <img src={activeImg} alt="Preview" />
            <span className="close-btn">×</span>
          </div>
        )}
      </section>

      {/* ===== CSS ===== */}
      <style>{`
/* ===== INNER BANNER ===== */
.inner-banner {
  position: relative;
  height: 420px;
  background: url("/assets/banner.jpg") center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  padding-top: 120px;
}

.inner-overlay {

  inset: 0;
  background: rgba(0,0,0,0.65);
}

.inner-content {
  position: relative;
  z-index: 1;
}

.inner-content h1 {
  font-size: 48px;
  font-weight: 700;
  font-family: "Merriweather", serif;
}

.breadcrumb {
  position: absolute;
  bottom: -20px;
  left: 55px;
  border-radius: 4px;
  background: var(--primary-color);
  padding: 10px 25px;
  font-size: 20px;
  font-weight: 500;
  color: #000;
  z-index: 1;
}
/* ===== CULTURAL SECTION ===== */
.cultural-section {
  padding: 90px 80px;
  background: #fff;
  overflow-x: hidden;
}

.cultural-header {
  text-align: center;
  max-width: 850px;
  margin: 0 auto 60px;
}

/* HEADER ANIMATION */
.reveal-top {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s ease;
}

.cultural-header h2 {
  font-size: 36px;
  font-family: "Merriweather", serif;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.cultural-header h2 span {
  width: 60px;
  height: 2px;
  background: #000;
}

.cultural-header p {
  font-size: 16px;
  color: #555;
  margin-top: 12px;
}

/* ===== IMAGE GRID ===== */
.cultural-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
}

/* IMAGE ANIMATION */
.reveal-img {
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: 12px;
  cursor: pointer;
  opacity: 0;
  transform: scale(0.9) translateY(30px);
  transition: all 0.8s ease;
}

.cultural-section.in-view .reveal-top,
.cultural-section.in-view .reveal-img {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.reveal-img:hover {
  transform: scale(1.05);
  box-shadow: 0 15px 35px rgba(0,0,0,0.25);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .cultural-section {
    padding: 70px 50px;
  }
}

@media (max-width: 992px) {
  .inner-banner {
    height: 300px;
    padding-top: 100px;
  }

  .inner-content h1 {
    font-size: 34px;
  }

  .breadcrumb {
    left: 30px;
    bottom: 15px;
    font-size: 13px;
  }

  .cultural-header h2 {
    font-size: 28px;
    flex-wrap: wrap;
  }

  .cultural-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }

  .reveal-img {
    height: 200px;
  }
}

@media (max-width: 768px) {
  .cultural-section {
    padding: 50px 20px;
  }

  .inner-banner {
    height: 420px;
    padding-top: 90px;
  }

  .inner-content h1 {
    font-size: 36px;
  }

  .breadcrumb {
    left: 16px;
    bottom: -20px;
    padding: 8px 12px;
  }

  .cultural-header {
    margin-bottom: 35px;
  }

  .cultural-header h2 {
    font-size: 24px;
  }

  .cultural-header h2 span {
    display: none;
  }

  .cultural-grid {
    grid-template-columns: 1fr;
  }

  .reveal-img {
    height: auto;
    aspect-ratio: 4 / 3;
  }
}

  /* ===== INNER BANNER ANIMATION ===== */

.inner-content {
  opacity: 0;
  transform: translateY(-40px);
  transition: all 0.9s ease;
}

.breadcrumb {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.9s ease;
}

/* Active */
.inner-banner.animate {
  opacity: 1;
  transform: scale(1);
}

.inner-banner.animate .inner-content,
.inner-banner.animate .breadcrumb {
  opacity: 1;
  transform: translateY(0);
}
  /* ===== IMAGE LIGHTBOX ===== */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.lightbox img {
  max-width: 90%;
  max-height: 90%;
  border-radius: 10px;
  animation: zoomIn 0.35s ease;
}

.close-btn {
  position: absolute;
  top: 25px;
  right: 30px;
  font-size: 42px;
  color: #fff;
  cursor: pointer;
  font-weight: 300;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes zoomIn {
  from { transform: scale(0.85); }
  to { transform: scale(1); }
}


      `}</style>
    </>
  );
}

export default Cultural;
